
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Link } from 'react-router-dom';
import { UserRound, Info, Eye, EyeOff } from 'lucide-react';
import { FcGoogle } from "react-icons/fc";

interface ContactAuthFormProps {
  step: number;
  loginData: any;
  registerData: any;
  isSubmitting: boolean;
  handleLoginChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleRegisterChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleLogin: (e: React.FormEvent) => void;
  handleRegister: (e: React.FormEvent) => void;
  handleGoogleSignIn: () => void;
}

const ContactAuthForm: React.FC<ContactAuthFormProps> = ({
  step,
  loginData,
  registerData,
  isSubmitting,
  handleLoginChange,
  handleRegisterChange,
  handleLogin,
  handleRegister,
  handleGoogleSignIn
}) => {
  const { t } = useTranslation();
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showRegisterPasswordConfirm, setShowRegisterPasswordConfirm] = useState(false);

  const validateIsraeliPhone = (phoneValue: string): boolean => {
    const phoneRegex = /^05\d{8}$/;
    return phoneRegex.test(phoneValue);
  };

  const handlePhoneInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Allow only digits, remove everything else
    const value = e.target.value.replace(/\D/g, '');
    // Limit to 10 digits (Israeli mobile format)
    const limitedValue = value.slice(0, 10);
    const syntheticEvent = {
      ...e,
      target: { ...e.target, value: limitedValue, name: 'phone' }
    } as React.ChangeEvent<HTMLInputElement>;
    handleRegisterChange(syntheticEvent);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="text-center mb-4">
        <UserRound className="mx-auto h-12 w-12 text-blue-500 mb-2" />
        <h3 className="text-lg font-medium text-gray-700">{t('requestForm.loginOrRegisterToSend')}</h3>
        <p className="text-sm text-gray-500">{t('requestForm.toContinueLoginOrRegister')}</p>
      </div>
      
      {/* Prominent info banner before Google sign-in */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-blue-800 text-sm">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium mb-1">
              {t('requestForm.formWillBeRestored', { defaultValue: 'הטופס ישוחזר לאחר ההתחברות' })}
            </p>
            <p className="text-blue-700">
              {t('requestForm.youCanAddImagesAfter', { defaultValue: 'תוכל להוסיף תמונות ולשלוח את הבקשה לאחר שתתחבר.' })}
            </p>
          </div>
        </div>
      </div>
      
      <Button type="button" variant="outline" className="w-full flex items-center justify-center gap-2 py-6" onClick={handleGoogleSignIn} disabled={isSubmitting}>
        <FcGoogle className="h-5 w-5" />
        <span>{t('auth.googleLogin')}</span>
      </Button>
      <div className="relative my-4">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t border-gray-300"></span>
        </div>
        <div className="relative flex justify-center text-xs">
          <span className="px-2 bg-white text-gray-500">{t('common.or')}</span>
        </div>
      </div>
      <Tabs defaultValue="login" className="w-full">
        <TabsList className="grid grid-cols-2 w-full">
          <TabsTrigger value="login">{t('auth.login')}</TabsTrigger>
          <TabsTrigger value="register">{t('auth.register')}</TabsTrigger>
        </TabsList>
        <TabsContent value="login">
          <form onSubmit={handleLogin} className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="login-email">{t('common.email')}</Label>
              <Input id="login-email" name="email" type="email" placeholder={t('loginForm.emailPlaceholder')} value={loginData.email} onChange={handleLoginChange} />
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label htmlFor="login-password">{t('common.password')}</Label>
                <Link to="/forgot-password" className="text-xs text-[#00D09E] hover:text-[#00C090]">
                  {t('common.forgotPassword')}
                </Link>
              </div>
              <div className="relative">
                <Input 
                  id="login-password" 
                  name="password" 
                  type={showLoginPassword ? "text" : "password"} 
                  placeholder={t('loginForm.passwordPlaceholder')} 
                  value={loginData.password} 
                  onChange={handleLoginChange} 
                />
                <button
                  type="button"
                  onClick={() => setShowLoginPassword(!showLoginPassword)}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                >
                  {showLoginPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <Button type="submit" disabled={isSubmitting} className="w-full bg-[#00D09E] hover:bg-[#00C090] text-white mt-4">
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <span className="inline-block h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {t('requestForm.loggingInAndSending')}
                </span>
              ) : (
                t('requestForm.loginAndSendRequest')
              )}
            </Button>
          </form>
        </TabsContent>
        <TabsContent value="register">
          <form onSubmit={handleRegister} className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="register-name">{t('registerForm.fullName')}</Label>
              <Input id="register-name" name="name" placeholder={t('registerForm.fullNamePlaceholder')} value={registerData.name} onChange={handleRegisterChange} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="register-email">{t('common.email')}</Label>
              <Input id="register-email" name="email" type="email" placeholder={t('registerForm.emailPlaceholder')} value={registerData.email} onChange={handleRegisterChange} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="register-email-confirm">{t('registerForm.emailConfirm')}</Label>
              <Input id="register-email-confirm" name="emailConfirm" type="email" placeholder={t('registerForm.emailConfirmPlaceholder')} value={registerData.emailConfirm || ''} onChange={handleRegisterChange} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="register-phone">{t('common.phone')}</Label>
              <Input 
                id="register-phone" 
                name="phone" 
                type="text" 
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={10}
                placeholder="0541234567" 
                value={registerData.phone} 
                onChange={handlePhoneInput} 
                dir="ltr" 
                className="text-start" 
              />
              <p className="text-sm text-muted-foreground">
                {t('auth.phoneFormatHint')}
              </p>
              {registerData.phone && !validateIsraeliPhone(registerData.phone) && (
                <p className="text-sm text-destructive">
                  {t('auth.invalidPhoneFormat')}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="register-password">{t('common.password')}</Label>
              <div className="relative">
                <Input 
                  id="register-password" 
                  name="password" 
                  type={showRegisterPassword ? "text" : "password"} 
                  placeholder={t('registerForm.passwordPlaceholder')} 
                  value={registerData.password} 
                  onChange={handleRegisterChange} 
                />
                <button
                  type="button"
                  onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                >
                  {showRegisterPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="register-password-confirm">{t('registerForm.passwordConfirm')}</Label>
              <div className="relative">
                <Input 
                  id="register-password-confirm" 
                  name="passwordConfirm" 
                  type={showRegisterPasswordConfirm ? "text" : "password"} 
                  placeholder={t('registerForm.passwordConfirmPlaceholder')} 
                  value={registerData.passwordConfirm} 
                  onChange={handleRegisterChange} 
                />
                <button
                  type="button"
                  onClick={() => setShowRegisterPasswordConfirm(!showRegisterPasswordConfirm)}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                >
                  {showRegisterPasswordConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div className="flex items-center space-x-2 space-x-reverse mt-2">
              <input type="checkbox" id="agree-terms" name="agreeTerms" checked={registerData.agreeTerms} onChange={handleRegisterChange} className="rounded border-gray-300 text-[#00D09E] focus:ring-[#00D09E]" />
              <Label htmlFor="agree-terms" className="text-sm cursor-pointer">
                {t('registerForm.agreeTerms')}
                <Link to="/terms" className="text-[#00D09E] mx-1 hover:underline">
                  {t('registerForm.termsOfService')}
                </Link>
                {t('registerForm.andThe')}
                <Link to="/privacy" className="text-[#00D09E] mx-1 hover:underline">
                  {t('registerForm.privacyPolicy')}
                </Link>
              </Label>
            </div>
            <Button type="submit" disabled={isSubmitting} className="w-full bg-[#00D09E] hover:bg-[#00C090] text-white mt-4">
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <span className="inline-block h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {t('requestForm.registeringAndSending')}
                </span>
              ) : (
                t('requestForm.registerAndSendRequest')
              )}
            </Button>
          </form>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ContactAuthForm;
